import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material';
import { DataGrid, trTR } from '@mui/x-data-grid';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getQualityProductControl, InsertQualityProductControl, deleteQualityProduct } from 'src/store/slices/dynamicCatalogSlice';
import moment from 'moment';
import { getUser } from 'src/utils/Common';
import AlertAndLoading from 'src/components/AlertAndLoading';
import {
  DARK,
  ORANGE,
  darkButtonSx,
  dialogPaperProps,
  inputSx,
  lightButtonSx,
  useDynamicStyles
} from 'src/components/dynamicCatalogStyles';
import DynamicListToolbar, { filterDynamicRows } from 'src/components/DynamicListToolbar';

function ProductQualityControlDynamic() {
  const classes = useDynamicStyles();
  const dispatch = useDispatch();
  const SessionUser = getUser();
  const [qualityProduct, setQualityProduct] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [AddQualityProductModelCode, setAddQualityProductModelCode] = React.useState("");
  const [updatePopUp, setUpdatePopUp] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [searchText, setSearchText] = React.useState('');
  const [responseMessage, setresponseMessage] = React.useState('');
  const [uyari, setUyari] = React.useState(false);
  const [alertType, setAlertType] = React.useState({ ErrorType: 'error', ErrorTitle: 'Uyarı' });

  const loadQualityProducts = () => {
    setLoading(true);
    dispatch(getQualityProductControl()).then(response => {
      // Son kayıt silindiğinde liste boş dönebilir; başarılı cevapta her zaman güncellenir
      if (response.meta?.requestStatus === 'fulfilled') {
        setQualityProduct(Array.isArray(response.payload) ? response.payload : []);
      }
      setLoading(false);
    })
  };

  useEffect(() => {
    loadQualityProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  function InfoAlert(ErrorDescription, ErrorType, ErrorTitle) {
    setresponseMessage(ErrorDescription);
    setAlertType({
      ErrorType: ErrorType,
      ErrorTitle: ErrorTitle
    })
    setUyari(true);
    return
  }

  function openInsert() {
    setAddQualityProductModelCode("");
    setUpdatePopUp(true);
  }

  function closeInsert() {
    if (saving) return;
    setUpdatePopUp(false);
    setAddQualityProductModelCode("");
  }

  function closeDelete() {
    if (saving) return;
    setDeleteTarget(null);
  }

  function AddQualityProductClick() {
    if (!AddQualityProductModelCode.trim()) {
      InfoAlert('Lütfen Model Kodunu Giriniz!...', 'error', 'Hata');
      return;
    }
    const data = {
      ModelCode: AddQualityProductModelCode.trim(),
      SicilNo: SessionUser?.UserId?.toString(),
    }
    setSaving(true)
    dispatch(InsertQualityProductControl(data)).then(response => {
      setSaving(false);
      if (response.payload?.ErrorCode && response.payload.ErrorCode !== '0') {
        InfoAlert(response.payload.ErrorDescription, 'error', 'Hata');
        return;
      }
      setUpdatePopUp(false);
      setAddQualityProductModelCode("");
      loadQualityProducts();
    })
  }

  function deleteQualityProductClick() {
    if (!deleteTarget) return;
    setSaving(true)
    dispatch(deleteQualityProduct([deleteTarget.MODEL_CODE, deleteTarget.ID])).then(response => {
      setSaving(false);
      setDeleteTarget(null);
      if (response.payload?.ErrorCode && response.payload.ErrorCode !== '0') {
        InfoAlert(response.payload.ErrorDescription, 'error', 'Hata');
      }
      loadQualityProducts();
    })
  }

  const uyariKapat = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setUyari(false);
  };

  const filteredRows = useMemo(() => filterDynamicRows(qualityProduct, {
    searchText,
    searchFields: ['MODEL_CODE', 'SICILNO']
  }), [qualityProduct, searchText]);

  const columns = [
    { field: 'MODEL_CODE', headerName: 'Model Kodu', flex: 1, minWidth: 160 },
    { field: 'SICILNO', headerName: 'Ekleyen Sicil No', flex: 1, minWidth: 140 },
    {
      field: 'CREATED_DATE', headerName: 'Eklenme Tarihi', width: 160,
      valueFormatter: (p) => (p.value ? moment(p.value).format('DD.MM.YYYY') : '')
    },
    {
      field: 'Sil', headerName: 'İşlem', width: 90, align: 'center', headerAlign: 'center',
      sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (cellValues) => (
        <Tooltip title="Sil">
          <IconButton size="small" onClick={() => setDeleteTarget(cellValues.row)}>
            <DeleteOutlineIcon sx={{ fontSize: 18, color: '#e53935' }} />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  return (
    <>
      <AlertAndLoading Message={responseMessage} open={uyari} AlertType={alertType} onClose={uyariKapat} />
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title="Ürün Kalite Problemleri"
          subheader={loading ? 'Yükleniyor…' : `${qualityProduct.length} model`}
          action={
            <Button startIcon={<AddIcon />} sx={darkButtonSx} onClick={openInsert}>
              Yeni Model
            </Button>
          }
        />
        <DynamicListToolbar
          searchText={searchText}
          onSearchChange={setSearchText}
          placeholder="Model kodu veya sicil no ara"
          shownCount={filteredRows.length}
        />
        <DataGrid
          className={classes.grid}
          style={{ height: 520, width: '100%' }}
          columns={columns}
          rows={filteredRows}
          loading={loading}
          rowHeight={44}
          headerHeight={44}
          disableSelectionOnClick
          getRowId={(row) => row.ID}
          localeText={{ ...trTR.components.MuiDataGrid.defaultProps.localeText, noRowsLabel: 'Kayıt bulunamadı.' }}
        />
      </Card>

      {/* Ekle */}
      <Dialog open={updatePopUp} onClose={closeInsert} fullWidth maxWidth="xs" PaperProps={dialogPaperProps}>
        <DialogTitle className={classes.dialogTitle}>
          Yeni Model
          <IconButton size="small" onClick={closeInsert} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
          <TextField
            label="Model Kodu"
            autoFocus
            fullWidth
            size="small"
            sx={inputSx}
            value={AddQualityProductModelCode}
            onChange={(e) => setAddQualityProductModelCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && AddQualityProductModelCode.trim()) AddQualityProductClick(); }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button sx={lightButtonSx} onClick={closeInsert} disabled={saving}>Vazgeç</Button>
          <Button
            sx={{ ...darkButtonSx, minWidth: 110 }}
            onClick={AddQualityProductClick}
            disabled={saving || !AddQualityProductModelCode.trim()}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: ORANGE }} /> : null}
          >
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme onayı */}
      <Dialog open={!!deleteTarget} onClose={closeDelete} fullWidth maxWidth="xs" PaperProps={dialogPaperProps}>
        <DialogTitle className={classes.dialogTitle}>
          Model Sil
          <IconButton size="small" onClick={closeDelete} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarningAmberIcon sx={{ color: ORANGE, mt: '2px' }} />
            <Box sx={{ fontSize: '13px', color: DARK }}>
              <b>{deleteTarget?.MODEL_CODE}</b> model kodu listeden silinecek. Emin misiniz?
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button sx={lightButtonSx} onClick={closeDelete} disabled={saving}>Vazgeç</Button>
          <Button
            sx={{ ...darkButtonSx, minWidth: 90, color: '#fff', backgroundColor: '#e53935', '&:hover': { backgroundColor: '#c62828' } }}
            onClick={deleteQualityProductClick}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProductQualityControlDynamic;
